<template>
  <div
    v-if="hasData"
    class="rounded-lg p-6 shadow-md border border-gray-200 dark:border-gray-700 dark:shadow-inner dark:shadow-gray-900 bg-white dark:bg-gray-800 transition-all"
  >
    <!-- Header with title and chart type toggle -->
    <div class="flex justify-between items-center mb-6">
      <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ title }}</h3>

      <!-- Chart Type Toggle -->
      <div
        class="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 p-1 bg-gray-100 dark:bg-gray-700"
      >
        <button
          @click="chartType = 'bar'"
          :class="[
            'px-4 py-2 rounded-md text-sm font-medium transition-all',
            chartType === 'bar'
              ? 'bg-primary text-white shadow-sm'
              : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
          ]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        </button>
        <button
          @click="chartType = 'pie'"
          :class="[
            'px-4 py-2 rounded-md text-sm font-medium transition-all',
            chartType === 'pie'
              ? 'bg-primary text-white shadow-sm'
              : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
          ]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Chart Container -->
    <div class="relative h-96 w-full">
      <Bar
        v-if="chartType === 'bar'"
        :key="`bar-${chartKey}`"
        class="w-full h-full"
        :data="chartData"
        :options="barOptions"
      />
      <Pie
        v-else
        :key="`pie-${chartKey}`"
        class="w-full h-full"
        :data="chartData"
        :options="pieOptions"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, withDefaults } from 'vue'
import { Bar, Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  type ChartOptions
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  ChartDataLabels
)

const props = withDefaults(
  defineProps<{
    title: string
    data: Record<string, number | string | boolean | any>
    isDark?: boolean
  }>(),
  {
    isDark: false
  }
)

const chartType = ref<'bar' | 'pie'>('bar')
const chartKey = ref(0)

const hasData = computed(() => {
  const entries = Object.entries(props.data || {})
  return entries.length > 0
})

const forceChartRerender = () => {
  chartKey.value += 1
}

onMounted(() => {
  window.addEventListener('resize', forceChartRerender)
  window.addEventListener('orientationchange', forceChartRerender)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', forceChartRerender)
  window.removeEventListener('orientationchange', forceChartRerender)
})

const axisColor = computed(() => (props.isDark ? '#e5e7eb' : '#6B7280'))
const gridColor = computed(() => (props.isDark ? 'rgba(255,255,255,0.15)' : 'rgba(156, 163, 175, 0.2)'))

// Process data for charts
const chartData = computed(() => {
  const entries = Object.entries(props.data)
  const labels = entries.map(([key]) => key)
  const values = entries.map(([, value]) => (typeof value === 'number' ? value : 0))

  return {
    labels,
    datasets: [
      {
        label: props.title,
        data: values,
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)', // blue
          'rgba(16, 185, 129, 0.7)', // green
          'rgba(251, 191, 36, 0.7)', // yellow
          'rgba(239, 68, 68, 0.7)', // red
          'rgba(139, 92, 246, 0.7)', // purple
          'rgba(236, 72, 153, 0.7)', // pink
          'rgba(20, 184, 166, 0.7)', // teal
          'rgba(249, 115, 22, 0.7)' // orange
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(251, 191, 36, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(139, 92, 246, 1)',
          'rgba(236, 72, 153, 1)',
          'rgba(20, 184, 166, 1)',
          'rgba(249, 115, 22, 1)'
        ],
        borderWidth: 2
      }
    ]
  }
})

const barOptions = computed<ChartOptions<'bar'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      padding: 12,
      titleFont: {
        size: 14
      },
      bodyFont: {
        size: 13
      }
    },
    datalabels: {
      color: '#ffffff',
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
      borderRadius: 4,
      padding: 2,
      borderWidth: 0,
      textAlign: 'center',
      anchor: 'center',
      align: 'center',
      clamp: true,
      clip: true,
      offset: 0,
      font: {
        weight: 'bold' as const,
        size: 11
      },
      formatter: (value: unknown) => {
        const numericValue = typeof value === 'number' ? value : Number(value) || 0
        return numericValue
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: gridColor.value
      },
      ticks: {
        color: axisColor.value
      }
    },
    x: {
      grid: {
        display: false
      },
      ticks: {
        color: axisColor.value
      }
    }
  }
}))

const pieOptions = computed<ChartOptions<'pie'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        padding: 15,
        font: {
          size: 12
        },
        color: axisColor.value,
        usePointStyle: true,
        pointStyle: 'circle'
      }
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      padding: 12,
      titleFont: {
        size: 14
      },
      bodyFont: {
        size: 13
      }
    },
    datalabels: {
      color: '#ffffff',
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
      borderRadius: 4,
      padding: 2,
      borderWidth: 0,
      textAlign: 'center',
      anchor: 'center',
      align: 'center',
      clamp: true,
      clip: true,
      offset: 0,
      font: {
        weight: 'bold' as const,
        size: 11
      },
      formatter: (value: unknown, context) => {
        const numericValue = typeof value === 'number' ? value : Number(value) || 0
        const dataset = context.chart.data.datasets?.[context.datasetIndex]
        const dataArray = (dataset?.data as number[]) ?? []
        const total = dataArray.reduce((sum, v) => sum + (typeof v === 'number' ? v : Number(v) || 0), 0)
        const percent = total > 0 ? ((numericValue / total) * 100).toFixed(1) : '0.0'
        return `${numericValue}\n(${percent}%)`
      }
    } as any
  }
}))
</script>

<style scoped></style>
